import type { MeasurementScale } from "@/hooks/useMeasurementScale";
import { MEASUREMENT_DRAW_RESULT_KINDS } from "@/lib/measurement-domain";
import {
  calculateLineLength,
  calculatePolygonAreaFeet,
  constrainToAngle,
  constrainToAxis,
  constrainToSquare,
  isValidLine,
  isValidPolygon,
  pixelsToFeet,
  type Point,
} from "@/lib/geometry";
import {
  computeValue,
  normalizeRect,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-helpers";
import {
  MEASUREMENT_GEOMETRY_TYPES,
  type DraftMeasurementState,
  type MeasurementGeometry,
  type PolygonGeometry,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-types";
import {
  MEASUREMENT_TYPES,
  TOOL_IDS,
  type MeasurementType,
  type ToolType,
} from "./registry";

type PageRectToScreenRect = (rect: {
  x: number;
  y: number;
  width: number;
  height: number;
}) => {
  x: number;
  y: number;
  width: number;
  height: number;
};

type PageToScreenPoint = (point: Point) => Point;

interface ToolDraftValidationArgs {
  draft: DraftMeasurementState;
  pageRectToScreenRect: PageRectToScreenRect;
  pageToScreenPoint: PageToScreenPoint;
}

interface ToolDraftPreviewArgs {
  vertices: Point[];
  startPoint: Point;
  pagePoint: Point;
  shiftKey: boolean;
  snapEnabled: boolean;
  measurementType: MeasurementType;
  height: number | undefined;
  scale: MeasurementScale | null;
  snapSegmentPoint: (anchor: Point, candidate: Point) => Point;
  snapRectangleCorner: (
    anchor: Point,
    candidate: Point,
    lockSquare: boolean
  ) => Point;
}

interface ToolResolveDrawingClickArgs {
  vertices: Point[];
  pagePoint: Point;
  shiftKey: boolean;
  snapEnabled: boolean;
  measurementType: MeasurementType;
  height: number | undefined;
  scale: MeasurementScale;
  getScreenDistance: (pointA: Point, pointB: Point) => number;
  snapSegmentPoint: (anchor: Point, candidate: Point) => Point;
}

export const TOOL_DRAW_RESULT_KINDS = {
  ...MEASUREMENT_DRAW_RESULT_KINDS,
} as const;

type ToolResolveDrawingClickResult =
  | { kind: typeof TOOL_DRAW_RESULT_KINDS.ignore }
  | {
      kind: typeof TOOL_DRAW_RESULT_KINDS.append;
      nextVertices: Point[];
      nextStartPoint: Point;
      draft: DraftMeasurementState;
    }
  | {
      kind: typeof TOOL_DRAW_RESULT_KINDS.finalize;
      draft: DraftMeasurementState;
    };

interface MeasurementToolBehavior {
  tool: ToolType;
  createInitialDraft: (args: {
    measurementType: MeasurementType;
    startPoint: Point;
    height: number | undefined;
  }) => DraftMeasurementState | null;
  buildPreviewDraft: (args: ToolDraftPreviewArgs) => DraftMeasurementState | null;
  resolveDrawingClick: (
    args: ToolResolveDrawingClickArgs
  ) => ToolResolveDrawingClickResult | null;
  validateDraft: (args: ToolDraftValidationArgs) => {
    isValid: boolean;
    error?: string;
  };
}

const lineBehavior: MeasurementToolBehavior = {
  tool: TOOL_IDS.line,
  createInitialDraft: ({ measurementType, startPoint, height }) => ({
    tool: TOOL_IDS.line,
    measurementType,
    geometry: { type: MEASUREMENT_GEOMETRY_TYPES.poly, vertices: [startPoint] },
    height,
    value: 0,
  }),
  buildPreviewDraft: ({
    vertices,
    pagePoint,
    shiftKey,
    snapEnabled,
    measurementType,
    height,
    scale,
    snapSegmentPoint,
  }) => {
    const lastVertex = vertices[vertices.length - 1];
    let currentPoint = pagePoint;

    if (shiftKey) {
      currentPoint = constrainToAxis(pagePoint, lastVertex);
    } else if (snapEnabled) {
      currentPoint = constrainToAngle(pagePoint, lastVertex);
    }

    currentPoint = snapSegmentPoint(lastVertex, currentPoint);
    const newVertices = [...vertices, currentPoint];

    let value = 0;
    if (scale) {
      const length = calculateLineLength(newVertices, scale.pageAspectRatio);
      if (measurementType === MEASUREMENT_TYPES.LF) {
        value = pixelsToFeet(length, scale.pixelsPerFoot);
      } else if (measurementType === MEASUREMENT_TYPES.SQFT && height) {
        value = pixelsToFeet(length, scale.pixelsPerFoot) * height;
      }
    }

    const geometry: PolygonGeometry = {
      type: MEASUREMENT_GEOMETRY_TYPES.poly,
      vertices: newVertices,
    };
    return {
      tool: TOOL_IDS.line,
      measurementType,
      geometry,
      height,
      value,
    };
  },
  resolveDrawingClick: ({
    vertices,
    pagePoint,
    shiftKey,
    snapEnabled,
    measurementType,
    height,
    scale,
    getScreenDistance,
    snapSegmentPoint,
  }) => {
    const lastVertex = vertices[vertices.length - 1];
    let currentPoint = pagePoint;

    if (shiftKey) {
      currentPoint = constrainToAxis(pagePoint, lastVertex);
    } else if (snapEnabled) {
      currentPoint = constrainToAngle(pagePoint, lastVertex);
    }

    currentPoint = snapSegmentPoint(lastVertex, currentPoint);
    const distanceToLast = getScreenDistance(currentPoint, lastVertex);

    if (distanceToLast < 1) {
      return { kind: TOOL_DRAW_RESULT_KINDS.ignore };
    }

    const geometry: PolygonGeometry = {
      type: MEASUREMENT_GEOMETRY_TYPES.poly,
      vertices: [...vertices, currentPoint],
    };
    const value = computeValue(
      TOOL_IDS.line,
      measurementType,
      geometry,
      height,
      scale.pixelsPerFoot,
      scale.pageAspectRatio
    );

    return {
      kind: TOOL_DRAW_RESULT_KINDS.finalize,
      draft: {
        tool: TOOL_IDS.line,
        measurementType,
        geometry,
        height,
        value,
      },
    };
  },
  validateDraft: ({ draft, pageToScreenPoint }) => {
    const poly = draft.geometry as PolygonGeometry;
    if (poly.vertices.length !== 2) {
      return { isValid: false, error: "Line must have exactly 2 vertices" };
    }
    const screenVertices = poly.vertices.map((vertex) => pageToScreenPoint(vertex));
    if (!isValidLine(screenVertices)) {
      return { isValid: false, error: "Line is too short" };
    }
    return { isValid: true };
  },
};

const polylineBehavior: MeasurementToolBehavior = {
  ...lineBehavior,
  tool: TOOL_IDS.polyline,
  createInitialDraft: ({ measurementType, startPoint, height }) => ({
    tool: TOOL_IDS.polyline,
    measurementType,
    geometry: { type: MEASUREMENT_GEOMETRY_TYPES.poly, vertices: [startPoint] },
    height,
    value: 0,
  }),
  buildPreviewDraft: (args) => {
    const draft = lineBehavior.buildPreviewDraft(args);
    return draft ? { ...draft, tool: TOOL_IDS.polyline } : null;
  },
  resolveDrawingClick: ({
    vertices,
    pagePoint,
    shiftKey,
    snapEnabled,
    measurementType,
    height,
    scale,
    getScreenDistance,
    snapSegmentPoint,
  }) => {
    const lastVertex = vertices[vertices.length - 1];
    let currentPoint = pagePoint;

    if (shiftKey) {
      currentPoint = constrainToAxis(pagePoint, lastVertex);
    } else if (snapEnabled) {
      currentPoint = constrainToAngle(pagePoint, lastVertex);
    }

    currentPoint = snapSegmentPoint(lastVertex, currentPoint);
    const distanceToLast = getScreenDistance(currentPoint, lastVertex);

    if (distanceToLast < 8 && vertices.length >= 2) {
      const geometry: PolygonGeometry = {
        type: MEASUREMENT_GEOMETRY_TYPES.poly,
        vertices,
      };
      const value = computeValue(
        TOOL_IDS.polyline,
        measurementType,
        geometry,
        height,
        scale.pixelsPerFoot,
        scale.pageAspectRatio
      );
      return {
        kind: TOOL_DRAW_RESULT_KINDS.finalize,
        draft: {
          tool: TOOL_IDS.polyline,
          measurementType,
          geometry,
          height,
          value,
        },
      };
    }

    if (distanceToLast < 1) {
      return { kind: TOOL_DRAW_RESULT_KINDS.ignore };
    }

    const nextVertices = [...vertices, currentPoint];
    const geometry: PolygonGeometry = {
      type: MEASUREMENT_GEOMETRY_TYPES.poly,
      vertices: nextVertices,
    };
    const value = computeValue(
      TOOL_IDS.polyline,
      measurementType,
      geometry,
      height,
      scale.pixelsPerFoot,
      scale.pageAspectRatio
    );

    return {
      kind: TOOL_DRAW_RESULT_KINDS.append,
      nextVertices,
      nextStartPoint: currentPoint,
      draft: {
        tool: TOOL_IDS.polyline,
        measurementType,
        geometry,
        height,
        value,
      },
    };
  },
  validateDraft: ({ draft, pageToScreenPoint }) => {
    const poly = draft.geometry as PolygonGeometry;
    const screenVertices = poly.vertices.map((vertex) => pageToScreenPoint(vertex));
    if (!isValidLine(screenVertices)) {
      return { isValid: false, error: "Line is too short" };
    }
    return { isValid: true };
  },
};

const rectangleBehavior: MeasurementToolBehavior = {
  tool: TOOL_IDS.rectangle,
  createInitialDraft: () => null,
  buildPreviewDraft: ({
    startPoint,
    pagePoint,
    shiftKey,
    measurementType,
    height,
    scale,
    snapRectangleCorner,
  }) => {
    const constrainedPoint = shiftKey
      ? constrainToSquare(pagePoint, startPoint)
      : pagePoint;
    const snappedRectanglePoint = snapRectangleCorner(
      startPoint,
      constrainedPoint,
      shiftKey
    );
    const geometry = normalizeRect(startPoint, snappedRectanglePoint);
    let value = 0;
    if (scale && geometry.width > 0 && geometry.height > 0) {
      value = computeValue(
        TOOL_IDS.rectangle,
        measurementType,
        geometry,
        height,
        scale.pixelsPerFoot,
        scale.pageAspectRatio
      );
    }
    return {
      tool: TOOL_IDS.rectangle,
      measurementType,
      geometry,
      height,
      value,
    };
  },
  resolveDrawingClick: () => null,
  validateDraft: ({ draft, pageRectToScreenRect }) => {
    const rect = draft.geometry as MeasurementGeometry;
    const screenRect = pageRectToScreenRect({
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
    });
    if (screenRect.width < 5 || screenRect.height < 5) {
      return { isValid: false, error: "Rectangle is too small" };
    }
    return { isValid: true };
  },
};

const polygonBehavior: MeasurementToolBehavior = {
  tool: TOOL_IDS.polygon,
  createInitialDraft: ({ measurementType, startPoint, height }) => ({
    tool: TOOL_IDS.polygon,
    measurementType,
    geometry: { type: MEASUREMENT_GEOMETRY_TYPES.poly, vertices: [startPoint] },
    height,
    value: 0,
  }),
  buildPreviewDraft: ({
    vertices,
    pagePoint,
    shiftKey,
    snapEnabled,
    measurementType,
    height,
    scale,
    snapSegmentPoint,
  }) => {
    const lastVertex = vertices[vertices.length - 1];
    let currentPoint = pagePoint;

    if (shiftKey) {
      currentPoint = constrainToAxis(pagePoint, lastVertex);
    } else if (snapEnabled) {
      currentPoint = constrainToAngle(pagePoint, lastVertex);
    }

    currentPoint = snapSegmentPoint(lastVertex, currentPoint);
    const newVertices = [...vertices, currentPoint];

    let value = 0;
    if (scale) {
      if (measurementType === MEASUREMENT_TYPES.LF) {
        value = pixelsToFeet(
          calculateLineLength(newVertices, scale.pageAspectRatio),
          scale.pixelsPerFoot
        );
      } else if (measurementType === MEASUREMENT_TYPES.SQFT) {
        value = calculatePolygonAreaFeet(
          newVertices,
          scale.pixelsPerFoot,
          scale.pageAspectRatio
        );
      } else if (measurementType === MEASUREMENT_TYPES.CF && height) {
        value =
          calculatePolygonAreaFeet(
            newVertices,
            scale.pixelsPerFoot,
            scale.pageAspectRatio
          ) * height;
      }
    }

    const geometry: PolygonGeometry = {
      type: MEASUREMENT_GEOMETRY_TYPES.poly,
      vertices: newVertices,
    };
    return {
      tool: TOOL_IDS.polygon,
      measurementType,
      geometry,
      height,
      value,
    };
  },
  resolveDrawingClick: ({
    vertices,
    pagePoint,
    shiftKey,
    snapEnabled,
    measurementType,
    height,
    scale,
    getScreenDistance,
    snapSegmentPoint,
  }) => {
    const lastVertex = vertices[vertices.length - 1];
    let currentPoint = pagePoint;

    if (shiftKey) {
      currentPoint = constrainToAxis(pagePoint, lastVertex);
    } else if (snapEnabled) {
      currentPoint = constrainToAngle(pagePoint, lastVertex);
    }

    const firstVertex = vertices[0];
    const isClosingPolygon =
      vertices.length >= 3 && getScreenDistance(currentPoint, firstVertex) < 8;
    if (isClosingPolygon) {
      currentPoint = firstVertex;
    } else {
      currentPoint = snapSegmentPoint(lastVertex, currentPoint);
    }

    const distanceToLast = getScreenDistance(currentPoint, lastVertex);
    const distanceToFirst = getScreenDistance(currentPoint, firstVertex);

    if (distanceToFirst < 8 && vertices.length >= 3) {
      const geometry: PolygonGeometry = {
        type: MEASUREMENT_GEOMETRY_TYPES.poly,
        vertices,
      };
      const value = computeValue(
        TOOL_IDS.polygon,
        measurementType,
        geometry,
        height,
        scale.pixelsPerFoot,
        scale.pageAspectRatio
      );
      return {
        kind: TOOL_DRAW_RESULT_KINDS.finalize,
        draft: {
          tool: TOOL_IDS.polygon,
          measurementType,
          geometry,
          height,
          value,
        },
      };
    }

    if (distanceToLast < 1) {
      return { kind: TOOL_DRAW_RESULT_KINDS.ignore };
    }

    const nextVertices = [...vertices, currentPoint];
    const geometry: PolygonGeometry = {
      type: MEASUREMENT_GEOMETRY_TYPES.poly,
      vertices: nextVertices,
    };
    const value = computeValue(
      TOOL_IDS.polygon,
      measurementType,
      geometry,
      height,
      scale.pixelsPerFoot,
      scale.pageAspectRatio
    );

    return {
      kind: TOOL_DRAW_RESULT_KINDS.append,
      nextVertices,
      nextStartPoint: currentPoint,
      draft: {
        tool: TOOL_IDS.polygon,
        measurementType,
        geometry,
        height,
        value,
      },
    };
  },
  validateDraft: ({ draft, pageToScreenPoint }) => {
    const poly = draft.geometry as PolygonGeometry;
    const screenVertices = poly.vertices.map((vertex) => pageToScreenPoint(vertex));
    const validation = isValidPolygon(screenVertices, true);
    if (!validation.isValid) {
      return { isValid: false, error: validation.error ?? "Invalid polygon" };
    }
    return { isValid: true };
  },
};

const TOOL_BEHAVIORS: Record<ToolType, MeasurementToolBehavior> = {
  [TOOL_IDS.line]: lineBehavior,
  [TOOL_IDS.polyline]: polylineBehavior,
  [TOOL_IDS.rectangle]: rectangleBehavior,
  [TOOL_IDS.polygon]: polygonBehavior,
};

export function getMeasurementToolBehavior(tool: ToolType): MeasurementToolBehavior {
  return TOOL_BEHAVIORS[tool];
}
